import { useEffect, useState } from "react";
import { Search, Plane } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Panel, Pill, PageLoader, Empty, inputCls } from "@/components/kit";
import { api, errMsg, fmtDate } from "@/lib/api";
import { SectionHead } from "@/pages/manage/common";

const STATUSES = ["new", "contacted", "booked", "closed"];
const TONE = { new: "volt", contacted: "amber", booked: "green", closed: "red" };

const toCsv = (rows) => {
  const cols = ["created_at", "team_name", "name", "email", "phone", "destination", "message", "status"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c] || (c === "status" ? "new" : ""))).join(","))].join("\n");
};

export default function TravelEnquiries() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  useEffect(() => {
    api.get("/platform/travel-enquiries").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  const update = async (r, s) => {
    try {
      await api.patch(`/platform/travel-enquiries/${r.id}`, { status: s });
      setRows(rows.map((x) => (x.id === r.id ? { ...x, status: s } : x)));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const exportCsv = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([toCsv(shown)], { type: "text/csv" }));
    a.download = "travel-enquiries.csv";
    a.click();
  };
  if (!rows) return <PageLoader />;
  const shown = rows.filter((r) => (!status || (r.status || "new") === status)
    && `${r.name} ${r.email} ${r.team_name} ${r.destination}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <SectionHead title="Travel enquiries" text={`${rows.length} holiday enquiries from every team's Partners in Travel page · ${rows.filter((r) => (r.status || "new") === "new").length} new`}>
        <button onClick={exportCsv} className="rounded-full border border-white/15 px-4 py-2 text-sm font-bold text-white hover:bg-white/5" data-testid="travel-export-btn">Export CSV</button>
      </SectionHead>
      <div className="flex flex-wrap gap-3">
        <div className="relative w-full max-w-xs"><Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" /><Input className="pl-9" placeholder="Search name, email, team" value={q} onChange={(e) => setQ(e.target.value)} data-testid="travel-search-input" /></div>
        <select className={`${inputCls} w-44`} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="travel-status-filter"><option value="">All statuses</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
      </div>
      {!shown.length && <Empty icon={Plane} title="No enquiries" text="Holiday enquiries from team hubs appear here, even if the email notification fails." />}
      <div className="space-y-3">
        {shown.map((r) => (
          <Panel key={r.id} data-testid={`travel-enquiry-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-white">{r.name} <span className="font-normal text-slate-400">· <a href={`mailto:${r.email}`} className="hover:text-white">{r.email}</a>{r.phone && ` · ${r.phone}`}</span></p>
                <p className="mt-1 text-sm text-slate-300"><Pill>{r.team_name}</Pill> <span className="ml-2">{r.destination || "No destination given"}</span></p>
                {r.message && <p className="mt-2 whitespace-pre-line text-sm text-slate-400">{r.message}</p>}
                <p className="mt-2 font-mono text-xs text-slate-500">{fmtDate(r.created_at)}</p>
              </div>
              <div className="flex items-center gap-2">
                <Pill tone={TONE[r.status || "new"]}>{r.status || "new"}</Pill>
                <select className={`${inputCls} h-9 w-36`} value={r.status || "new"} onChange={(e) => update(r, e.target.value)} data-testid={`travel-status-select-${r.id}`}>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
              </div>
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}
